import apiCall from "auth/ApiCallInstance";
import { urlBase64ToUint8Array } from "../utils/pushClient";
import { API_BASE_URL } from "configs/AppConfig";
import { AUTH_TOKEN } from "constants/AuthConstant";

const API = API_BASE_URL;

// Wait for authentication token to be available
async function waitForAuth(maxWait = 10000) {
  const startTime = Date.now();
  while (Date.now() - startTime < maxWait) {
    const token = localStorage.getItem(AUTH_TOKEN);
    if (token) {
      console.log("[push] Auth token found");
      return true;
    }
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  console.warn("[push] Auth token not found after waiting");
  return false;
}

// Fetch the VAPID public key, retrying once if the stored token was stale
// (a 401 here used to silently kill the whole refresh).
async function fetchPublicKey() {
  try {
    const { data } = await apiCall.get(`${API}/push/public-key`);
    return data?.publicKey || null;
  } catch (e) {
    if (e?.response?.status === 401) {
      console.warn("[push] public-key returned 401 (stale token?), retrying in 3s...");
      await new Promise((resolve) => setTimeout(resolve, 3000));
      try {
        const { data } = await apiCall.get(`${API}/push/public-key`);
        return data?.publicKey || null;
      } catch (retryErr) {
        console.error(
          "[push] public-key retry failed — session likely expired, push refresh skipped:",
          retryErr?.response?.status || retryErr?.message
        );
        return null;
      }
    }
    console.error("[push] public-key fetch failed:", e?.response?.data || e?.message);
    return null;
  }
}

export async function enablePushForAdmin() {
  try {
    console.log("[push] start");

    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      console.log("[push] not supported");
      return;
    }

    // Wait for authentication before proceeding
    const hasAuth = await waitForAuth();
    if (!hasAuth) {
      console.log("[push] No auth token, skipping push subscription");
      return;
    }

    // Fetch the key BEFORE registering, so the SW registration URL can carry
    // the API base + VAPID key. The service worker needs both to handle
    // `pushsubscriptionchange` (subscription rotation) on its own, without
    // access to app config or auth.
    const publicKey = await fetchPublicKey();
    if (!publicKey) return;
    console.log("[push] got public key", publicKey.slice(0, 12) + "...");

    const swUrl = `/sw.js?api=${encodeURIComponent(API)}&vapid=${encodeURIComponent(publicKey)}`;
    const reg = await navigator.serviceWorker.register(swUrl, { scope: "/" });
    await navigator.serviceWorker.ready;

    if (!navigator.serviceWorker.controller) {
      await new Promise((resolve) => {
        navigator.serviceWorker.addEventListener("controllerchange", resolve, { once: true });
      });
    }

    console.log("[push] sw ready + controlling");

    const perm = await Notification.requestPermission();
    console.log("[push] permission:", perm);
    if (perm !== "granted") return;

    let sub = await reg.pushManager.getSubscription();
    console.log("[push] existing sub?", !!sub);

    // If the existing subscription was created with a DIFFERENT VAPID key
    // (e.g. server keys were rotated), every push to it fails with 403 and it
    // never self-heals. Detect the mismatch and re-subscribe fresh.
    if (sub && sub.options?.applicationServerKey) {
      const want = urlBase64ToUint8Array(publicKey);
      const have = new Uint8Array(sub.options.applicationServerKey);
      const sameKey = have.length === want.length && have.every((b, i) => b === want[i]);
      if (!sameKey) {
        console.warn("[push] VAPID key mismatch on existing subscription — re-subscribing");
        try {
          await sub.unsubscribe();
        } catch (e) {
          console.warn("[push] unsubscribe failed (continuing):", e?.message);
        }
        sub = null;
      }
    }

    if (!sub) {
      try {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
        console.log("[push] subscribe OK");
      } catch (e) {
        console.error("[push] subscribe FAIL:", e);
        return;
      }
    }

    // ✅ Convert to clean JSON before sending
    const plainSub = sub.toJSON ? sub.toJSON() : JSON.parse(JSON.stringify(sub));
    console.log("[push] plainSub:", plainSub);

    console.log("[push] posting subscription to server...");
    try {
      const r = await apiCall.post(`${API}/push/subscribe`, plainSub);
      console.log("[push] server saved:", r?.data);
    } catch (e) {
      console.error("[push] POST /subscribe failed:", e?.response?.data || e);
      // If 401, token might have expired - wait a bit and retry once
      if (e?.response?.status === 401) {
        console.log("[push] Got 401, waiting for token refresh and retrying...");
        await new Promise(resolve => setTimeout(resolve, 2000));
        try {
          const retry = await apiCall.post(`${API}/push/subscribe`, plainSub);
          console.log("[push] server saved (retry):", retry?.data);
        } catch (retryError) {
          console.error("[push] Retry also failed:", retryError?.response?.data || retryError);
          return;
        }
      } else {
        return;
      }
    }

    console.log("[push] done ✅");
  } catch (e) {
    console.error("[push] unexpected error:", e);
  }
}
